// JOBE-0044.9: shared create/rename modal for user-saved views. The
// design pins a small centered card with one text input, a read-only
// chip echo, and Save / Cancel buttons. Enter submits; Escape cancels.
//
// `mode` switches the title and submit button label only; both flows
// produce the same `onSubmit({ name })` callback shape. The host owns
// the network call so the modal stays focused on input + state.

import React, { useEffect, useRef, useState } from 'react';

export function SavedViewModal({
  mode = 'create',
  initialName = '',
  chips = [],
  onSubmit,
  onCancel,
}) {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (onCancel) onCancel();
    }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = (name || '').trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ name: trimmed });
    } catch (err) {
      setError(err?.message || String(err));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  const title = mode === 'rename' ? 'Rename view' : 'Save this view';
  const submitLabel = mode === 'rename' ? 'Save' : 'Save view';

  return (
    <div
      className="saved-view-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="saved-view-modal"
      onKeyDown={onKeyDown}
    >
      <form className="saved-view-modal-card card" onSubmit={submit}>
        <header className="saved-view-modal-head">
          <h3 className="lane-title">{title}</h3>
          <button
            type="button"
            className="saved-view-modal-close"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="saved-view-modal-body">
          <label
            htmlFor="saved-view-modal-name"
            className="saved-view-modal-label"
          >
            Name
          </label>
          <input
            ref={inputRef}
            id="saved-view-modal-name"
            type="text"
            className="saved-view-modal-input"
            placeholder="Name this view…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={256}
            autoComplete="off"
          />
          {Array.isArray(chips) && chips.length > 0 ? (
            <div className="saved-view-modal-chips" aria-label="Filter preview">
              <span className="saved-view-modal-chips-label">filters</span>
              <ul className="saved-view-modal-chips-list">
                {chips.map((c, i) => (
                  <li key={i} className="saved-view-modal-chip">
                    {chipLabel(c)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {error ? (
            <div className="saved-view-modal-error" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <footer className="saved-view-modal-foot">
          <button
            type="button"
            className="saved-view-modal-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="saved-view-modal-submit"
            disabled={!(name || '').trim() || submitting}
          >
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </footer>
      </form>
    </div>
  );
}

// chipLabel renders a compact one-line summary of a chip — `friendly:value`
// or just `value` for the scope chip. The modal echoes the chip set as a
// read-only preview; full rendering belongs to the chip row component.
function chipLabel(chip) {
  if (!chip || typeof chip !== 'object') return '';
  if (chip.kind === 'scope') return 'everything I can read';
  const token = chip.friendlyToken || chip.wirePredicate || '';
  const values = Array.isArray(chip.values) ? chip.values.join(', ') : '';
  if (!token) return values;
  return `${token}: ${values}`;
}
